/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/* eslint-disable no-unused-vars */
/* eslint-disable react/no-unknown-property */
import React from 'react';
import { Card, Row, Col, Spin, Tag, Descriptions, Alert, Collapse } from 'antd';
import { UserOutlined, IdcardOutlined, BookOutlined, DownOutlined } from '@ant-design/icons';
import { Building } from 'lucide-react';
import dayjs from 'dayjs';
import { themeColor } from '../../../../../config/theme/themeVariables';

const { Panel } = Collapse;

const CompanyInformation = ({ companyData, loading, error }) => {
  // Helper function to check if renewal is expired
  const isRenewalExpired = (renewalDate) => {
    if (!renewalDate) return false;
    return dayjs(renewalDate).isBefore(dayjs());
  };

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).format('DD/MM/YYYY');
  };

  // Helper function to get subscription status
  const getSubscriptionStatus = () => {
    if (!companyData?.renewal_valid_upto) {
      return { text: 'No Subscription', color: themeColor['warning-color'] };
    }

    if (isRenewalExpired(companyData.renewal_valid_upto)) {
      return { text: 'Subscription Expired', color: themeColor['danger-color'] };
    }

    return { text: 'Active', color: themeColor['success-color'] };
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <Spin size="large" />
        <p className="mt-4 text-gray-600">Loading company information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <Alert
          message="Error Loading Company Information"
          description={error}
          type="error"
          showIcon
          className="text-center"
        />
      </div>
    );
  }

  if (!companyData) {
    return null;
  }

  const subscriptionStatus = getSubscriptionStatus();

  return (
    <div className="mb-8">
      <style jsx>{`
          .company-info .ant-descriptions-item-content {
            font-weight: 500 !important;
            font-size: 14px !important;
            color: #000000 !important;
          }
          
          .company-info .ant-descriptions-item-label {
            font-weight: 400 !important;
            font-size: 13px !important;
            color:rgb(101, 104, 110) !important;
          }
          
          .company-info .ant-tag {
            font-weight: 600 !important;
            font-size: 12px !important;
            padding: 4px 12px !important;
            border-radius: 6px !important;
            height: auto !important;
            line-height: 1.4 !important;
          }
          
          .company-info .ant-descriptions-item-content .ant-tag {
            font-weight: 600 !important;
            font-size: 12px !important;
            padding: 4px 12px !important;
            border-radius: 6px !important;
            height: auto !important;
            line-height: 1.4 !important;
          }
          
          .company-accordion .ant-collapse-header {
            background: linear-gradient(135deg, #f8fafc, #e2e8f0);
            border-radius: 8px !important;
            font-weight: 600 !important;
            font-size: 16px !important;
          }
          
          .company-accordion .ant-collapse-content {
            background: white;
            border-radius: 0 0 8px 8px;
          }
          
          .company-accordion .ant-collapse-item {
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            margin-bottom: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
        `}</style>

      <Collapse
        defaultActiveKey={['1']}
        className="company-accordion"
        expandIcon={({ isActive }) => (
          <DownOutlined rotate={isActive ? 180 : 0} className='text-gray-500' />
        )}
      >
        <Panel
          header={
            
            <div className="flex items-center">
              <Building className="mr-2 text-xl" />
              <span className="text-lg font-semibold">Company Information</span>
            </div>
          }
          key="1"
        >
          <div className="company-info">
            <Descriptions
              title="Company Details"
              bordered
              column={{ xxl: 4, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
              size="middle"
            >
              <Descriptions.Item label="Company Name" span={2}>
                {companyData.title || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                <Tag color={companyData.status ? "green" : "red"}>
                  {companyData.status ? "Active" : "Inactive"}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Membership Number">
                {companyData.membership_number || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Owner Name">
                {companyData.owner_name || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Email">
                {companyData.email || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Website">
                {companyData.website || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Registration Date">
                {formatDate(companyData.registration_date)}
              </Descriptions.Item>

              <Descriptions.Item label="Province">
                {companyData.province_name || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="City">
                {companyData.city_name || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Documents Uploaded">
                <Tag color={companyData.all_docs_uploaded === true ? themeColor['success-color'] : themeColor['danger-color']} >
                  <p className='!text-white'>{companyData.all_docs_uploaded === true ? "Yes" : "No"}</p>
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Subscription Status">
                <Tag color={subscriptionStatus.color}>
                  <p className='!text-white'>{subscriptionStatus.text}</p>
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Last Renewed">
                {formatDate(companyData.renewed_at)}
              </Descriptions.Item>

              <Descriptions.Item label="Renewal Valid Upto">
                {formatDate(companyData.renewal_valid_upto)}
              </Descriptions.Item>

              <Descriptions.Item label="Created At">
                {formatDate(companyData.created_at)}
              </Descriptions.Item>

              <Descriptions.Item label="Last Updated">
                {formatDate(companyData.updated_at)}
              </Descriptions.Item>

              <Descriptions.Item label="HQ Address" span={3}>
                {companyData.hq_address || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Owner Contact">
                {companyData.owner_contact || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="HQ Contact">
                {companyData.hq_contact || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

export default CompanyInformation; 